import { CSSProperties } from 'react'

import { Skeleton } from 'antd'
import styled from 'styled-components'

type ContentProps = {
  column?: boolean
  justify?: CSSProperties['justifyContent']
  align?: CSSProperties['alignItems']
  gap?: CSSProperties['gap']
}

export const WideContent = styled.div<ContentProps>`
  width: 100%;
  display: flex;
  flex-direction: ${({ column }) => (column ? 'column' : 'row')};
  justify-content: ${({ justify }) => justify || 'initial'};
  align-items: ${({ align }) => align || 'initial'};
  gap: ${({ gap }) => gap || 'initial'};

  ${({ theme: { media, breakpoints } }) => media.md`
  max-width: ${breakpoints.lg}px;
    margin-left: auto;
    margin-right: auto;
  `}
`

export const NarrowContent = styled.div<ContentProps>`
  width: 100%;
  display: flex;
  flex-direction: ${({ column }) => (column ? 'column' : 'row')};
  justify-content: ${({ justify }) => justify || 'initial'};
  align-items: ${({ align }) => align || 'initial'};
  gap: ${({ gap }) => gap || 'initial'};

  ${({ theme: { media, breakpoints } }) => media.md`
    max-width: ${breakpoints.md}px;
    margin-left: auto;
    margin-right: auto;
  `}
`

const ActionsWrapper = styled(WideContent)`
  justify-content: flex-start;
  margin-top: 0.5rem;
  margin-bottom: 1rem;

  ${({ theme: { media } }) => media.md`
    justify-content: flex-end;
  `}
`

const BottomActionsWrapper = styled(ActionsWrapper)`
  margin-top: 1rem;
`

const InfoWrapper = styled(WideContent)`
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 2.25rem;
  justify-content: space-between;
  padding-bottom: 0.625rem;
  border-bottom: 1px solid ${({ theme }) => theme.colour.secondary._300};
`

const InfoSpacer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  ${({ theme: { media } }) => media.sm`
  flex-direction: row;
    align-items: center;
    gap: 1rem;
  `}
`

const Title = styled.span`
  ${({ theme }) => theme.font.heading02}
  width: 100%;
  color: ${({ theme }) => theme.colour.secondary._900};
  margin-bottom: 0.25rem;
  white-space: pre-line;
  display: block;
  word-wrap: break-word;
  overflow-wrap: anywhere;
`

const TitleAboveInfo = styled.div`
  ${({ theme }) => theme.font.heading04}
  width: 100%;
  color: ${({ theme }) => theme.colour.secondary._900};
  margin-bottom: 0.75rem;
`

const Description = styled.span`
  ${({ theme }) => theme.font.body02}
  width: 100%;
  color: ${({ theme }) => theme.colour.secondary._900};
  white-space: pre-line;
  display: block;
`

const BeforeContent = styled(WideContent)`
  margin-top: 1rem;
`

const ChildrenWrapper = styled.div`
  margin-top: 2rem;
  padding-bottom: 3rem;
`

export type Props = {
  actions?: React.ReactNode
  infoLeft?: React.ReactNode
  infoRight?: React.ReactNode
  titleAboveInfo?: React.ReactNode
  title?: React.ReactNode
  description?: React.ReactNode
  beforeContent?: React.ReactNode
  extraDetails?: React.ReactNode
  children?: React.ReactNode
  loading?: boolean
  wide?: boolean
  hideTopActions?: boolean
  bottomActions?: boolean
}

export const CorePageViewEntity: React.FC<Props> = ({
  actions,
  infoLeft,
  infoRight,
  titleAboveInfo,
  title,
  description,
  beforeContent,
  extraDetails,
  loading,
  wide,
  hideTopActions,
  bottomActions,
  children,
}) => {
  const showInfo = !!infoLeft || !!infoRight
  const hasTitleOrDescription = !!title || !!description

  const ContentWrapper = wide ? WideContent : NarrowContent

  if (loading) {
    return (
      <>
        {actions && !hideTopActions && (
          <ActionsWrapper>
            <Skeleton.Input active size="small" />
          </ActionsWrapper>
        )}
        {showInfo && (
          <InfoWrapper>
            <InfoSpacer>
              <Skeleton.Input active size="small" />
              <Skeleton.Input active size="small" />
            </InfoSpacer>
            <InfoSpacer>
              <Skeleton.Input active size="small" />
            </InfoSpacer>
          </InfoWrapper>
        )}
        <ContentWrapper column>
          <Title>
            <Skeleton.Input
              active
              block
              size="small"
              style={{ maxWidth: '300px' }}
            />
          </Title>
        </ContentWrapper>
      </>
    )
  }

  return (
    <>
      {!!actions && !hideTopActions && (
        <ActionsWrapper as={ContentWrapper}>{actions}</ActionsWrapper>
      )}

      {titleAboveInfo && (
        <ContentWrapper>
          <TitleAboveInfo>{titleAboveInfo}</TitleAboveInfo>
        </ContentWrapper>
      )}

      {showInfo && (
        <InfoWrapper as={ContentWrapper}>
          <InfoSpacer>{infoLeft}</InfoSpacer>
          <InfoSpacer>{infoRight}</InfoSpacer>
        </InfoWrapper>
      )}

      {beforeContent && <BeforeContent>{beforeContent}</BeforeContent>}

      <ContentWrapper column>
        {title && !titleAboveInfo && <Title>{title}</Title>}
        <Description>{description}</Description>
        {extraDetails && (
          <div style={{ marginTop: hasTitleOrDescription ? '2.5rem' : '1rem' }}>
            {extraDetails}
          </div>
        )}
      </ContentWrapper>

      {children && (
        <ChildrenWrapper>
          {children}

          {bottomActions && actions && (
            <BottomActionsWrapper as={ContentWrapper}>
              {actions}
            </BottomActionsWrapper>
          )}
        </ChildrenWrapper>
      )}
    </>
  )
}
